import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { FiSearch } from 'react-icons/fi';
import { API_URL } from '../../Api/api';
import { getAuthToken } from '../../Api/authToken';
import './ChatInbox.css';

const headers = () => ({ Authorization: `Bearer ${getAuthToken()}` });

const SendIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
    <path fill="currentColor" d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
  </svg>
);

const formatTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
};

const PartnerChat = () => {
  const { pathname } = useLocation();
  const self = pathname.startsWith('/partner');
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = self ? 0 : Number(searchParams.get('partner') || 0);
  const [query, setQuery] = useState('');
  const [partners, setPartners] = useState([]);
  const [messages, setMessages] = useState([]);
  const [thread, setThread] = useState(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [denied, setDenied] = useState(false);
  const logRef = useRef(null);
  const open = self || selectedId > 0;

  useEffect(() => {
    if (self) return undefined;
    let cancelled = false;
    const load = () => {
      axios.get(`${API_URL}/admin/chat/partners`, {
        headers: headers(),
        params: query.trim() ? { q: query.trim() } : {},
      }).then((res) => {
        if (!cancelled) setPartners(Array.isArray(res.data?.partners) ? res.data.partners : []);
      }).catch(() => {});
    };
    const timer = setTimeout(load, 200);
    const poll = setInterval(load, 4000);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      clearInterval(poll);
    };
  }, [query, self]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        if (self) {
          const res = await axios.get(`${API_URL}/admin/chat/mine`, { headers: headers() });
          if (cancelled) return;
          setDenied(false);
          setMessages(Array.isArray(res.data?.messages) ? res.data.messages : []);
          setThread(res.data?.thread || null);
          axios.post(`${API_URL}/admin/chat/mine/read`, {}, { headers: headers() }).catch(() => {});
          return;
        }
        if (!selectedId) {
          setMessages([]);
          return;
        }
        const res = await axios.get(`${API_URL}/admin/chat/partners/${selectedId}/messages`, { headers: headers() });
        if (cancelled) return;
        setMessages(Array.isArray(res.data?.messages) ? res.data.messages : []);
        setThread(res.data?.thread || null);
        axios.post(`${API_URL}/admin/chat/partners/${selectedId}/read`, {}, { headers: headers() }).catch(() => {});
      } catch (error) {
        if (!cancelled && error.response?.status === 403) setDenied(true);
      }
    };
    load();
    const timer = setInterval(load, 2000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [selectedId, self]);

  useEffect(() => {
    const node = logRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [messages]);

  const selected = useMemo(
    () => partners.find((partner) => partner.id === selectedId) || null,
    [partners, selectedId],
  );

  const send = async (event) => {
    event.preventDefault();
    const body = text.trim();
    if (!body || sending || (!self && !selectedId)) return;
    setSending(true);
    try {
      const url = self
        ? `${API_URL}/admin/chat/mine/messages`
        : `${API_URL}/admin/chat/partners/${selectedId}/messages`;
      const res = await axios.post(url, { body }, { headers: headers() });
      setMessages(Array.isArray(res.data?.messages) ? res.data.messages : []);
      setText('');
      if (!self && selectedId) {
        const staff = res.data?.partner || {};
        setPartners((prev) => prev.map((partner) => (
          partner.id === selectedId
            ? {
              ...partner,
              last_message: body,
              last_message_at: new Date().toISOString(),
              last_staff_name: staff.last_staff_name || partner.last_staff_name,
              last_staff_role: staff.last_staff_role || partner.last_staff_role,
            }
            : partner
        )));
      }
      if (res.data?.thread) setThread(res.data.thread);
    } catch (error) {
      if (error.response?.status === 403) setDenied(true);
    } finally {
      setSending(false);
    }
  };

  if (denied) {
    return (
      <div className="admin-page">
        <div className="page-header">
          <h1>Chat</h1>
          <p>Chat access is not enabled for this account.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page chat-inbox-page">
      <div className="page-header">
        <h1>{self ? 'Chat Inbox' : 'Partner Chat'}</h1>
        <p>{self ? 'Messages with the admin team. Replies work when Access Chat is on.' : 'Every partner is listed here. This chat is between the partner and admin.'}</p>
      </div>
      <div className={`chat-inbox${open ? ' thread-open' : ''}${self ? ' is-solo' : ''}`}>
        {!self && (
          <aside className="chat-thread-list">
            <div className="chat-list-search">
              <FiSearch aria-hidden="true" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search partners"
                aria-label="Search partners"
              />
            </div>
            {partners.length === 0 && <p className="chat-empty">No partners found.</p>}
            {[...partners].sort((a, b) => {
              const aAt = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
              const bAt = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
              if (bAt !== aAt) return bAt - aAt;
              return String(a.name || '').localeCompare(String(b.name || ''));
            }).map((partner) => (
              <button
                type="button"
                key={partner.id}
                className={`chat-thread${partner.id === selectedId ? ' is-active' : ''}`}
                onClick={() => setSearchParams({ partner: String(partner.id) })}
              >
                <span className="chat-thread__top">
                  <strong>{partner.name || 'Partner'}</strong>
                  {partner.admin_unread > 0 && <em>{partner.admin_unread}</em>}
                </span>
                <span className="chat-thread__preview">
                  {partner.last_message || (partner.chat_access ? 'No message yet' : 'Chat access off')}
                </span>
                {partner.last_staff_name ? <span className="chat-thread__staff">Last: {partner.last_staff_name}</span> : null}
                <span className="chat-thread__time">{formatTime(partner.last_message_at)}</span>
              </button>
            ))}
          </aside>
        )}
        <section className="chat-pane">
          {!open && <p className="chat-empty">Select a partner to start chatting.</p>}
          {open && (
            <>
              <header className="chat-pane__head">
                <strong>{self ? (thread?.last_admin_name || 'Admin') : (selected?.name || 'Partner')}</strong>
                {!self && selected && (
                  <p>
                    {selected.email}
                    {selected.last_staff_name ? ` · Last: ${selected.last_staff_name}` : ''}
                    {selected.chat_access ? '' : ' · Chat access is off, so this partner cannot reply yet.'}
                  </p>
                )}
              </header>
              <div className="chat-pane__log" ref={logRef}>
                {messages.filter((message) => message.sender !== 'system' && !message.deleted).map((message) => {
                  const mine = self ? message.sender === 'user' : message.sender === 'admin';
                  const side = mine ? 'admin' : 'user';
                  return (
                    <div key={message.id} className={`chat-msg-row chat-msg-row--${side}`}>
                      <div className={`chat-msg chat-msg--${side}`}>
                        {message.sender_name ? <span className="chat-msg__who">{message.sender_name}</span> : null}
                        <span>{message.body}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
              <form className="chat-pane__compose" onSubmit={send}>
                <input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Write a message"
                  aria-label="Message"
                />
                <button type="submit" disabled={sending || !text.trim()} aria-label="Send">
                  <SendIcon />
                </button>
              </form>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default PartnerChat;
